/**
 * 公司稅務資料卡片（統一編號 / 公司登記名稱 / 稅籍編號）。
 * 租戶（我的租戶帳單）與平台（帳單管理）共用：讀寫同一組欄位、同一套驗證。
 * 驗證與錯誤（ERR450015/029/030/031/032）由後端回傳，showApiError 自動解析代碼。
 * 樂觀鎖：存檔帶讀取時的 updated_at（expected_updated_at）；衝突後端回 409（ERR450031）。
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';

import { useMessage } from '../../../contexts/MessageContext';
import {
  InvoiceProfileSave, TenantInvoiceProfile,
} from '../../../services/platformBillingService';

interface Props {
  profile: TenantInvoiceProfile | null;
  canEdit: boolean;
  onSave: (data: InvoiceProfileSave) => Promise<TenantInvoiceProfile>;
  onSaved?: (p: TenantInvoiceProfile) => void;
  /** 無 update 權限時顯示的說明（例：「如需修改請洽貴公司組織管理員」）。 */
  readonlyHint?: string;
}

const TaxProfileCard: React.FC<Props> = ({ profile, canEdit, onSave, onSaved, readonlyHint }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`platformBilling.${k}`, d);
  const { showSuccess, showWarning, showApiError } = useMessage();

  const [taxId, setTaxId] = useState('');
  const [title, setTitle] = useState('');
  const [reg, setReg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTaxId(profile?.tax_id || '');
    setTitle(profile?.invoice_title || '');
    setReg(profile?.tax_registration_no || '');
  }, [profile]);

  // 卡片底部單行：「最後修改：… 由{方} {人} · {開立說明}」。無修改紀錄時只顯示說明。
  const bottomLine = () => {
    const hint = tt('taxProfileHint', '發票會以這裡的資料開立。已開立的發票不受修改影響。');
    if (!profile?.updated_at) return hint;
    const side = profile.updated_side === 'platform'
      ? tt('sidePlatform', '平台') : tt('sideTenant', '貴公司');
    const who = profile.updated_by_name ? ` ${profile.updated_by_name}` : '';
    const when = new Date(profile.updated_at).toLocaleString();
    const updated = tt('taxProfileUpdatedAt', '最後修改') + `：${when} ${tt('by', '由')}${side}${who}`;
    return `${updated} · ${hint}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await onSave({
        tax_id: taxId.trim() || null,
        invoice_title: title.trim() || null,
        tax_registration_no: reg.trim() || null,
        expected_updated_at: profile?.updated_at || null,
      });
      showSuccess('SYS020001', { name: tt('taxProfile', '公司稅務資料') });
      // lint-msg-allow: 代碼由後端回傳（SYS450016·統編與他租戶重複·非阻斷提示）
      if (saved.tax_id_warning) showWarning(saved.tax_id_warning);
      onSaved?.(saved);
    } catch (err) {
      showApiError(err);     // ERR450015/029/030/032（400）、ERR450031（409 樂觀鎖衝突）
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {tt('taxProfile', '公司稅務資料')}
      </Typography>
      {/* 桌機一列（統編固定寬 / 公司登記名稱自動拉長 / 稅籍編號固定寬 / 儲存靠右）；手機改為一欄一列、儲存在最下。 */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <TextField size="small" label={tt('taxId', '統一編號')} value={taxId}
          onChange={e => setTaxId(e.target.value)} disabled={!canEdit || saving}
          inputProps={{ maxLength: 8 }} sx={{ width: { xs: '100%', md: 150 }, flexShrink: 0 }} />
        <TextField size="small" label={tt('companyRegName', '公司登記名稱')} value={title}
          onChange={e => setTitle(e.target.value)} disabled={!canEdit || saving}
          inputProps={{ maxLength: 200 }} sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }} />
        <TextField size="small" label={tt('taxRegistrationNo', '稅籍編號')} value={reg}
          onChange={e => setReg(e.target.value)} disabled={!canEdit || saving}
          inputProps={{ maxLength: 9 }}
          placeholder={tt('taxRegNoPlaceholder', '9 碼（選填）')}
          sx={{ width: { xs: '100%', md: 180 }, flexShrink: 0 }} />
        {canEdit && (
          // height 40 = 對齊 size="small" 輸入框高度，桌機同列不上下錯位
          <Button variant="contained" size="small" onClick={save} disabled={saving}
            sx={{ flexShrink: 0, height: 40, width: { xs: '100%', md: 'auto' } }}>
            {tt('save', '儲存')}
          </Button>
        )}
      </Stack>
      {/* 卡片底部一行：最後修改 + 開立說明合併；無 update 權限時顯示唯讀提示。 */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {bottomLine()}
        </Typography>
        {!canEdit && readonlyHint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {readonlyHint}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default TaxProfileCard;
